<template>
  <v-container>
    <v-layout justify-center>
      <v-flex>
        <v-card class="form">
          <v-app-bar dark color="primary">
            <h3>{{ $t('editProfileScreen.title') }}</h3>
          </v-app-bar>
          <ValidationObserver ref="editProfileForm" v-slot="{ invalid }">
            <v-form @submit.prevent="onSubmit">
              <v-card-text>
                <template v-if="hasComponents">
                  <template v-for="(component, componentIndex) in components">
                    <div
                      :key="componentIndex"
                      v-show="isValidSection(component)"
                      class="form-layout"
                    >
                      <h3 v-if="component.props.title">
                        {{ component.props.title }}
                      </h3>
                      <v-container v-if="component.props.children" class="pt-6">
                        <v-row
                          v-for="(child, childIndex) in component.props.children"
                          :key="childIndex"
                        >
                          <template
                            v-for="(childComponent, childComponentIndex) in child.components"
                          >
                            <v-col
                              v-if="isSatisfyingRequirements(childComponent.requirements)"
                              :key="childComponentIndex"
                              :cols="child.layout === 'column' ? 12 : 'undefined'"
                            >
                              <editable-custom-field
                                v-if="childComponent.props.type === 'custom_user_field' && isCustomFieldEditable(childComponent.props.key)"
                                :field="mapCustomField(childComponent)"
                                :field-value="getCustomFieldValue(childComponent.props.key)"
                                @input="setCustomFieldValue"
                              />
                              <editable-user-field
                                v-if="childComponent.props.type !== 'custom_user_field'"
                                :field="childComponent"
                                :field-value="getFieldValue(childComponent.props.key)"
                                @input="setFieldValue"
                              />
                            </v-col>
                          </template>
                        </v-row>
                      </v-container>
                    </div>
                  </template>
                </template>
                <template v-else>
                  <template
                    v-for="(userField, index) in defaultUserFields"
                  >
                    <editable-user-field
                      :key="`user-field-${index}`"
                      v-if="userField.props.key !== 'address'"
                      :field="userField"
                      :field-value="getFieldValue(userField.props.key)"
                      @input="setFieldValue"
                    />
                  </template>
                  <template
                    v-for="(customUserField, index) in editableCustomUserFields"
                  >
                    <editable-custom-field
                      :key="`custom-field-${index}`"
                      :field="customUserField"
                      :field-value="getCustomFieldValue(customUserField.key)"
                      @input="setCustomFieldValue"
                    />
                  </template>
                </template>
              </v-card-text>
              <v-card-actions class="mr-5 mb-5">
                <v-spacer></v-spacer>
                <v-btn
                  type="submit"
                  color="primary"
                  :disabled="invalid || loading"
                  :loading="loading"
                >
                  {{ $t('editProfileScreen.profileSaveButtonText') }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </ValidationObserver>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog v-model="showDialog" max-width="340px">
      <v-card>
        <v-card-title>
          <h3>{{ dialogTitle }}</h3>
        </v-card-title>
        <v-card-text>
          <p>{{ dialogMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="showDialogSecondaryAction"
            color="primary"
            text
            @click="dialogSecondaryAction"
          >
            {{ $t('editProfileScreen.changeSocialMediaEmailAlertCancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="dialogPrimaryAction"
          >
            {{ $t('editProfileScreen.changeSocialMediaEmailAlertConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from 'lodash'
import {mapGetters, mapActions} from 'vuex'
import {scrollTo} from '@/utils/html'
import EditableUserField from '@/components/profile/EditableUserField'
import EditableCustomField from '@/components/profile/EditableCustomField'

export default {
  components: {
    EditableUserField,
    EditableCustomField,
  },
  data() {
    return {
      DEFAULT_FIELDS: [
        'profile_picture',
        'email',
        'first_name',
        'last_name',
        'customer_id',
        'birthday',
        'gender',
        'address'
      ],
      email: '',
      first_name: '',
      last_name: '',
      customer_id: '',
      street: '',
      city: '',
      city_code: '',
      street_number: '',
      state: '',
      country: '',
      gender: '',
      birthday: '',
      customFields: {},
      loading: false,
      errorMessage: '',
      showDialog: false,
      showDialogSecondaryAction: false,
      dialogTitle: '',
      dialogMessage: '',
      dialogPrimaryAction: () => {},
      dialogSecondaryAction: () => {},
    }
  },
  computed: {
    ...mapGetters([
      'authSettings',
      'navigationSettings',
      'user',
      'customUserFields',
      'appSettings',
      'isEmailAddressChangeable',
    ]),
    defaultUserFields() {
      return this.DEFAULT_FIELDS.map((fieldKey) => {
        return {
          props: {
            key: fieldKey
          }
        }
      })
    },
    components() {
      return _.get(
        this.navigationSettings, 'screens.materialEditProfileScreen.components', []
      )
    },
    hasComponents() {
      return !_.isEmpty(this.components)
    },
    editableCustomUserFields() {
      return this.customUserFields.filter((field) => {
        return _.get(field, 'editable') && _.get(field, 'show_in_app')
      })
    },
    initialCustomUserFieldValues() {
      const fieldValues = {}
      const userFields = _.defaultTo(_.get(this.user, 'custom_fields'), [])

      for (const customUserField of this.editableCustomUserFields) {
        const userField = userFields.filter((field) => {
          return field.key === customUserField.key
        })[0]
        let defaultValue = _.get(customUserField, 'metadata.default', '')
        let value = _.get(userField, 'value', defaultValue)

        if (customUserField.type === 'select') {
          const choices = _.get(customUserField, ['metadata', 'choices'])
          defaultValue = choices.filter((choice) => {
            return !!_.has(choice, 'default')
          })[0]
          value = choices.filter((choice) => {
            return choice.value === value
          })[0]
          value = _.isUndefined(value) ? defaultValue : value
          value = _.get(value, 'value', '')
        }

        if (customUserField.type === 'select' && _.isUndefined(value)) {
          continue
        }

        if (customUserField.type === 'bool' && !_.isBoolean(value)) {
          continue
        }

        fieldValues[customUserField.key] = value
      }

      return fieldValues
    },
    loginProvider() {
      let loginProvider = ''

      if (_.get(this.user, 'is_logged_in_with_facebook', false)) {
        loginProvider = 'Facebook'
      } else if (_.get(this.user, 'is_logged_in_with_google', false)) {
        loginProvider = 'Google'
      } else if (_.get(this.user, 'is_logged_in_with_apple_id', false)) {
        loginProvider = 'Apple'
      }

      return loginProvider
    },
    isEmailChanged() {
      return _.get(this.user, 'email', '') !== _.get(this, 'email', '')
    },
  },
  created() {
    this.loadInitialData()
  },
  methods: {
    ...mapActions(['updateUserProfile']),
    loadInitialData() {
      this.email = this.user.email
      this.first_name = this.user.first_name
      this.last_name = this.user.last_name
      this.customer_id = this.user.customer_id
      this.street = _.get(this.user, 'address.street', '')
      this.city = _.get(this.user, 'address.city', '')
      this.city_code = _.get(this.user, 'address.city_code', '')
      this.street_number = _.get(this.user, 'address.street_number', '')
      this.state = _.get(this.user, 'address.state', '')
      this.country = _.get(this.user, 'address.country', '')
      this.gender = this.user.gender
      this.birthday = this.user.birthday
      this.customFields = this.initialCustomUserFieldValues
    },
    getFieldValue(key) {
      return _.get(this, key, '')
    },
    getCustomFieldValue(key) {
      return _.get(this.customFields, key, '')
    },
    setFieldValue(payload) {
      if (_.has(this, _.get(payload, 'key', ''), false)) {
        this[payload.key] = _.get(payload, 'value', '')
      }
    },
    setCustomFieldValue(payload) {
      this.$set(
        this.customFields,
        _.get(payload, 'key', ''),
        _.get(payload, 'value', '')
      )
    },
    mapCustomField(field) {
      const customUserField = this.editableCustomUserFields.find((customField) => {
        return customField.key === field.props.key
      })

      if (customUserField) {
        return {
          ...field,
          ...customUserField
        }
      }

      return field
    },
    isValidSection(component) {
      const name = _.get(component, 'name', '')
      return _.isString(name) && name.toLocaleLowerCase() === 'section'
    },
    isSatisfyingRequirements(requirements) {
      if (!_.isArray(requirements) || _.isEmpty(requirements)) {
        return true
      }

      return !requirements.some((requirement) => {
        const keyPath = _.get(requirement, 'key', '')
        const alowedValues = _.get(requirement, 'allowed_values', [])
        let value = ''

        if (!keyPath) {
          return true
        }

        if (!_.isArray(alowedValues)) {
          return true
        }

        const payloadSegments = keyPath.split('.')

        if (payloadSegments[0] === 'user') {
          payloadSegments.shift()
          value = _.get(this.user, payloadSegments.join('.'), '')
        }

        if (payloadSegments[0] === 'custom_user_field') {
          const lookupKey = payloadSegments[payloadSegments.length - 1]
          const customUserField = this.customUserFields.find(
           (customField) => {
             return customField.key === lookupKey
           }
          )

          value = _.get(customUserField, 'value', '')
        }

        return !alowedValues.some((alowedValue) => {
          return alowedValue === value
        })
      })
    },
    isCustomFieldEditable(key) {
      const customUserField = this.editableCustomUserFields.find(
        (customField) => {
          return customField.key === key
        })

      if (customUserField) {
        return true
      }

      return false
    },
    async onSubmit() {
      if (
        this.isEmailAddressChangeable
          && this.isEmailChanged
          && !_.isEmpty(this.loginProvider)
      ) {
        this.showChangeSocialMediaEmailDialog()
      } else {
        await this.finishProfileUpdate()
      }
    },
    async finishProfileUpdate() {
      this.closeDialog()

      const isValid = this.$refs.editProfileForm.validate()

      if (!isValid) {
        return
      }

      let updates = {}

      this.DEFAULT_FIELDS.forEach((key) => {
        updates[key] = this[key]
      })

      updates = _.omit(updates, ['profile_picture'])

      const addressFields = {
        city: this.city,
        city_code: this.city_code,
        street: this.street,
        street_number: this.street_number,
        state: this.state,
        country: this.country,
      }

      updates = _.omit(updates, Object.keys(addressFields))

      const customFields = []
      for (let [key, value] of Object.entries(this.customFields)) {
        if (_.has(value, 'value')) {
          value = value.value
        }
        if (_.isObject(value) && _.has(value, 'id')) {
          customFields.push({key, value: _.get(value, 'id')})
        } else {
          customFields.push({key, value})
        }
      }

      if (!this.isEmailAddressChangeable || _.isEmpty(this.email)) {
        updates = _.omit(updates, ['email'])
      }

      try {
        this.loading = true

        const preUpdateEmail = _.get(this.user, 'email')

        const response = await this.updateUserProfile({
          ...updates,
          custom_fields: customFields,
          address: addressFields,
        })

        const postUpdateEmail = _.get(response, 'email')

        if (preUpdateEmail !== postUpdateEmail) {
          this.showChangedEmailDialog(postUpdateEmail)
        }

        this.loading = false
        scrollTo(0)
      } catch (error) {
        this.loading = false
        this.showErrorDialog()
      }
    },
    showChangeSocialMediaEmailDialog() {
      this.dialogTitle =
          this.$t('editProfileScreen.changeSocialMediaEmailAlertTitle')
        this.dialogMessage =
          this.$t('editProfileScreen.changeSocialMediaEmailAlertMessage', {
            loginProvider: this.loginProvider
          })
        this.showDialogSecondaryAction = true
        this.dialogSecondaryAction = this.closeDialog
        this.dialogPrimaryAction = this.finishProfileUpdate
        this.showDialog = true
    },
    showChangedEmailDialog(postUpdateEmail) {
      this.dialogTitle = this.$t('editProfileScreen.changedEmailAlertTitle')
      this.dialogMessage = this.$t('editProfileScreen.changedEmailAlertText', {
        postUpdateEmail
      })
      this.dialogPrimaryAction = this.closeDialog
      this.showDialog = true
    },
    showErrorDialog() {
      this.dialogTitle = this.$t('errorMessages.errorDialogHeader')
      this.dialogMessage = this.$t('errorMessages.default')
      this.dialogPrimaryAction = this.closeDialog
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
      this.dialogTitle = ''
      this.dialogMessage = ''
      this.showDialogSecondaryAction = false
      this.dialogPrimaryAction = () => {}
      this.dialogSecondaryAction = () => {}
    },
  },
}
</script>

<style lang="scss" scoped>
.form {
  max-width: 720px;
  margin: 0 auto;
}

.form-layout {
  ::v-deep .row {
    margin-right: 0;
    margin-left: 0;
  }

  ::v-deep .col {
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>
